// ─────────────────────────────────────────────────────────────────────────────
// Controle de atividade do operador (Ativo / Inativo).
//
// Cada mudança de status vira um PERÍODO em orion.operador_atividade: uma linha
// com início e fim. O expediente de cada um vem de orion.operador — quem não
// tem jornada cadastrada (diretoria) fica de fora do controle e nem vê a pílula.
//
// O período NÃO é fechado ao sair do app, de propósito: fechar o Orion não é
// mudar de status. Reabrindo no mesmo dia, o período continua de onde parou; em
// outro dia, `restaurarPeriodoAberto` fecha no fim da jornada do dia antigo.
//
// Portado do Orion 1.4.1 (renderer.js, bloco "Controle de atividade").
// ─────────────────────────────────────────────────────────────────────────────

import { tabela, tabelaOperador } from './supabase';
import { horaParaMinutos, dataLocalISO } from './datas';

const TOLERANCIA_PADRAO_MIN = 5;
// Teto do café. Fallback de quando o banco não responde — o valor real vem de
// orion.operador.ope_cafe_limite_min, ajustável por operador.
const CAFE_LIMITE_PADRAO_MIN = 15;
/** Minutos sem uso do Orion até o monitor marcar ociosidade. */
export const INATIVIDADE_LIMITE_MIN = 25;

/** Rótulo e ícone de cada motivo de inatividade (o mesmo texto da pílula). */
export const TIPOS = {
  almoco: { rotulo: 'Almoço', icone: 'coffee' },
  cafe: { rotulo: 'Café', icone: 'coffee' },
  apoio: { rotulo: 'Apoio a colega', icone: 'users' },
  ociosidade: { rotulo: 'Ociosidade', icone: 'clock' },
  fora_expediente: { rotulo: 'Fora do expediente', icone: 'moon' },
  outro: { rotulo: 'Outro', icone: 'pen' },
};

/** Tamanho máximo da justificativa do tipo "outro". Curto: é rótulo, não relato. */
export const JUSTIFICATIVA_MAX = 20;

/**
 * Pausas que geram aviso de retorno.
 * Apoio a colega e fora do expediente não têm tempo de referência — não avisam.
 */
const AVISO_RETORNO_TIPOS = ['ociosidade', 'almoco', 'cafe'];
/** Minutos de excesso ainda tratados como "pode melhorar". */
const MARGEM_ATENCAO_MIN = 10;

/** Tabela dos períodos. */
const tabelaAtividade = () => tabela('operador_atividade');

// ── Expediente ───────────────────────────────────────────────────────────────

/**
 * Jornada do operador. Devolve { expediente, naoCadastrado }.
 *
 * Três desfechos, e a diferença importa para a interface:
 *   sem registro na tabela → provável falha de cadastro, o operador precisa saber
 *   registro sem jornada   → diretoria: controle desligado de propósito, sem aviso
 *   registro com jornada   → controle normal
 */
export async function carregarExpediente(email) {
  if (!email) return { expediente: null, naoCadastrado: false };
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_codigo, ope_jornada_ini, ope_jornada_fim, ope_almoco_ini, ope_almoco_fim, ope_tolerancia_min, ope_cafe_limite_min')
      // ilike sem curinga = comparação exata sem diferenciar maiúsculas: alguns
      // e-mails estão gravados em caixa alta na tabela.
      .ilike('ope_email', String(email).trim())
      .maybeSingle();

    if (error || !data) return { expediente: null, naoCadastrado: true };
    if (!data.ope_jornada_ini) return { expediente: null, naoCadastrado: false };

    return {
      naoCadastrado: false,
      expediente: {
        codigo: data.ope_codigo,
        ini: horaParaMinutos(data.ope_jornada_ini),
        fim: horaParaMinutos(data.ope_jornada_fim),
        almocoIni: horaParaMinutos(data.ope_almoco_ini),
        almocoFim: horaParaMinutos(data.ope_almoco_fim),
        tolerancia: Number.isFinite(data.ope_tolerancia_min) ? data.ope_tolerancia_min : TOLERANCIA_PADRAO_MIN,
        cafeLimite: Number.isFinite(data.ope_cafe_limite_min) ? data.ope_cafe_limite_min : CAFE_LIMITE_PADRAO_MIN,
      },
    };
  } catch (err) {
    console.warn('[Orion] Expediente:', err.message);
    return { expediente: null, naoCadastrado: false };
  }
}

/**
 * Colegas oferecidos no tipo "apoio": só quem tem jornada (setor de suporte),
 * está ativo no cadastro e não é o próprio operador logado.
 */
export async function carregarColegas(email) {
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_codigo, ope_nome, ope_sobrenome, ope_email')
      .eq('ope_enabled', true)
      .not('ope_jornada_ini', 'is', null)
      .order('ope_nome');
    if (error || !Array.isArray(data)) return [];

    const meu = String(email || '').trim().toLowerCase();
    return data
      .filter(o => String(o.ope_email || '').trim().toLowerCase() !== meu)
      .map(o => ({ codigo: o.ope_codigo, nome: `${o.ope_nome} ${o.ope_sobrenome || ''}`.trim() }));
  } catch (err) {
    console.warn('[Orion] Colegas:', err.message);
    return [];
  }
}

// ── Cálculos ─────────────────────────────────────────────────────────────────

/** Minutos → texto curto ("48 min", "1h08"). Mesmo formato da pílula. */
export function formatarDuracao(min) {
  const m = Math.max(0, Math.round(min));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/**
 * Diferença entre o realizado e o previsto, em minutos, ao fechar uma pausa.
 * POSITIVO = passou do previsto. NEGATIVO = voltou antes.
 *
 * Almoço usa a janela cadastrada e respeita a tolerância. Café usa o teto de
 * ope_cafe_limite_min e NÃO tem tolerância: passar de 15 min já conta como
 * atraso — dar os mesmos 5 min de folga seria um terço da pausa inteira.
 */
export function calcularDesvioPausa(expediente, periodo, fim) {
  if (!expediente) return { tipo: null, min: null };
  const real = Math.round((fim - periodo.inicio) / 60000);

  if (periodo.tipo === 'almoco') {
    if (expediente.almocoIni == null || expediente.almocoFim == null) return { tipo: null, min: null };
    const dif = real - (expediente.almocoFim - expediente.almocoIni);
    return Math.abs(dif) > expediente.tolerancia ? { tipo: 'almoco', min: dif } : { tipo: null, min: null };
  }

  if (periodo.tipo === 'cafe') {
    const dif = real - expediente.cafeLimite;
    return dif > 0 ? { tipo: 'cafe', min: dif } : { tipo: null, min: null };
  }

  return { tipo: null, min: null };
}

/**
 * Tempo de referência de cada pausa, para o aviso de retorno comparar.
 *   previsto → o teto/janela combinada, em minutos
 *   folga    → quanto pode passar sem virar "pode melhorar"
 * null quando o tipo não tem referência (não gera aviso).
 */
export function referenciaPausa(expediente, tipo) {
  if (!expediente) return null;
  if (tipo === 'almoco') {
    if (expediente.almocoIni == null || expediente.almocoFim == null) return null;
    return {
      previsto: expediente.almocoFim - expediente.almocoIni,
      folga: expediente.tolerancia, rotuloRef: 'previsto',
    };
  }
  // Café e ociosidade não têm tolerância: o teto já é o combinado.
  if (tipo === 'cafe') return { previsto: expediente.cafeLimite, folga: 0, rotuloRef: 'limite' };
  if (tipo === 'ociosidade') {
    return { previsto: INATIVIDADE_LIMITE_MIN, folga: 0, rotuloRef: 'limite' };
  }
  return null;
}

/**
 * Texto do aviso mostrado quando o operador volta a ficar Ativo.
 * Devolve { titulo, corpo } ou null quando a pausa não tem referência.
 *
 * Três faixas:
 *   dentro do previsto (ou voltou antes) · passou pouco · passou disso
 */
export function montarAvisoRetorno(expediente, resumo) {
  if (!resumo || resumo.status !== 'inativo' || !AVISO_RETORNO_TIPOS.includes(resumo.tipo)) return null;

  const ref = referenciaPausa(expediente, resumo.tipo);
  if (!ref) return null;

  const info = TIPOS[resumo.tipo] || { rotulo: 'Pausa' };
  // `resumo.duracao` já É o tempo real da pausa — o período de ociosidade é
  // gravado desde o início da inatividade, e não desde o alerta de 25 min.
  // Somar esse limite aqui contaria os mesmos minutos duas vezes.
  const excedente = resumo.duracao - ref.previsto;   // POSITIVO passou · NEGATIVO voltou antes

  let tipoAviso, titulo;
  if (excedente <= ref.folga) { tipoAviso = 'ok'; titulo = 'Bem-vindo de volta'; }
  else if (excedente <= ref.folga + MARGEM_ATENCAO_MIN) { tipoAviso = 'info'; titulo = 'Pode melhorar'; }
  else { tipoAviso = 'erro'; titulo = 'Tempo acima do previsto'; }

  const previstoTxt = formatarDuracao(ref.previsto);
  let comparacao;
  if (excedente > 0) comparacao = `${formatarDuracao(excedente)} além do ${ref.rotuloRef} (${previstoTxt})`;
  else if (excedente < 0) comparacao = `${formatarDuracao(-excedente)} a menos que o ${ref.rotuloRef} (${previstoTxt})`;
  else comparacao = `exatamente o ${ref.rotuloRef} (${previstoTxt})`;

  // Avisa que houve atraso, sem falar em registro: o operador precisa saber do
  // atraso, não de como o Orion trata isso internamente. (Quem volta antes tem
  // minutos negativos e não é atraso.)
  const atraso = resumo.atrasoTipo && Number.isFinite(resumo.atrasoMin) && resumo.atrasoMin > 0
    ? ` Houve atraso de ${formatarDuracao(resumo.atrasoMin)}.`
    : '';

  return { tipo: tipoAviso, titulo, corpo: `${info.rotulo}: ${formatarDuracao(resumo.duracao)} — ${comparacao}.${atraso}` };
}

// ── Períodos ─────────────────────────────────────────────────────────────────

/**
 * True quando ainda não há nenhum período hoje — logo, o que está sendo aberto
 * agora é a chegada do operador, e serve para medir atraso de entrada.
 */
async function ehPrimeiroPeriodoDoDia(expediente, dataRef) {
  try {
    const { count, error } = await tabelaAtividade()
      .select('id', { count: 'exact', head: true })
      .eq('operador_codigo', expediente.codigo)
      .eq('data_ref', dataRef);
    return !error && (count || 0) === 0;
  } catch (e) { return false; }
}

/** Abre um período novo. Devolve o período em memória, ou null. */
export async function abrirPeriodo(expediente, operador, status, tipo, opts, agora) {
  if (!expediente) return null;
  const dataRef = dataLocalISO(agora);

  // Chegada: no primeiro período do dia, compara a hora real com o início da
  // jornada. A diferença fica na própria linha (atraso_tipo / atraso_min).
  let atrasoTipo = null;
  let atrasoMin = null;
  if (status === 'ativo' && await ehPrimeiroPeriodoDoDia(expediente, dataRef)) {
    const dif = (agora.getHours() * 60 + agora.getMinutes()) - expediente.ini;
    if (Math.abs(dif) > expediente.tolerancia) { atrasoTipo = 'chegada'; atrasoMin = dif; }
  }

  const registro = {
    operador_codigo: expediente.codigo,
    operador_nome: operador?.nome || null,
    operador_email: String(operador?.email || '').trim().toLowerCase() || null,
    status,
    tipo: status === 'inativo' ? tipo : null,
    origem: opts.origem === 'automatico' ? 'automatico' : 'manual',
    observacao: opts.observacao || null,
    apoio_operador_codigo: tipo === 'apoio' ? (opts.apoioCodigo || null) : null,
    apoio_operador_nome: tipo === 'apoio' ? (opts.apoioNome || null) : null,
    inicio: agora.toISOString(),
    data_ref: dataRef,
    atraso_tipo: atrasoTipo,
    atraso_min: atrasoMin,
  };

  try {
    const { data, error } = await tabelaAtividade().insert(registro).select('id').single();
    if (error) { console.warn('[Orion] Atividade (insert):', error.message); return null; }
    return {
      id: data.id,
      status,
      tipo: registro.tipo,
      inicio: agora,
      apoioCodigo: registro.apoio_operador_codigo,
      apoioNome: registro.apoio_operador_nome,
      observacao: registro.observacao,
    };
  } catch (err) {
    console.warn('[Orion] Atividade:', err.message);
    return null;
  }
}

/**
 * Fecha o período em aberto, gravando duração e desvio.
 * `marcarSaida` indica fim de dia — aí também compara com o fim da jornada.
 * Devolve o resumo do período fechado, para quem precisa dele (o aviso de retorno).
 */
export async function fecharPeriodo(expediente, periodo, fim, marcarSaida = false) {
  if (!periodo) return null;

  const duracao = Math.max(0, Math.round((fim - periodo.inicio) / 60000));
  let { tipo: atrasoTipo, min: atrasoMin } = calcularDesvioPausa(expediente, periodo, fim);

  // Saída: diferença entre a hora real de encerramento e o fim da jornada.
  // 'fora_expediente' fica de fora: a saída já foi registrada no período que ele
  // encerrou, e marcar de novo aqui inventaria um segundo atraso.
  if (marcarSaida && !atrasoTipo && expediente && periodo.tipo !== 'fora_expediente') {
    const dif = (fim.getHours() * 60 + fim.getMinutes()) - expediente.fim;
    if (Math.abs(dif) > expediente.tolerancia) { atrasoTipo = 'saida'; atrasoMin = dif; }
  }

  try {
    const patch = { fim: fim.toISOString(), duracao_min: duracao, atualizado_em: new Date().toISOString() };
    if (atrasoTipo) { patch.atraso_tipo = atrasoTipo; patch.atraso_min = atrasoMin; }
    const { error } = await tabelaAtividade().update(patch).eq('id', periodo.id);
    if (error) console.warn('[Orion] Atividade (update):', error.message);
  } catch (err) {
    console.warn('[Orion] Atividade:', err.message);
  }

  return { status: periodo.status, tipo: periodo.tipo, duracao, atrasoTipo, atrasoMin };
}

/**
 * No login, recupera o período que ficou em aberto.
 * Se for de um dia anterior (app fechado sem encerrar), fecha no fim da jornada
 * daquele dia e começa limpo. Devolve o período de hoje, ou null.
 */
export async function restaurarPeriodoAberto(expediente) {
  if (!expediente) return null;
  try {
    const { data, error } = await tabelaAtividade()
      .select('id, status, tipo, inicio, data_ref, apoio_operador_codigo, apoio_operador_nome, observacao')
      .eq('operador_codigo', expediente.codigo)
      .is('fim', null)
      .order('inicio', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;

    const periodo = {
      id: data.id,
      status: data.status,
      tipo: data.tipo,
      inicio: new Date(data.inicio),
      apoioCodigo: data.apoio_operador_codigo,
      apoioNome: data.apoio_operador_nome,
      observacao: data.observacao,
    };

    // Período de hoje: continua de onde parou.
    if (data.data_ref === dataLocalISO(new Date())) return periodo;

    // De outro dia: fecha no fim da jornada daquele dia e não restaura.
    const fim = new Date(periodo.inicio);
    fim.setHours(Math.floor(expediente.fim / 60), expediente.fim % 60, 0, 0);
    await fecharPeriodo(expediente, periodo, fim > periodo.inicio ? fim : periodo.inicio, true);
    return null;
  } catch (err) {
    console.warn('[Orion] Restaurar período:', err.message);
    return null;
  }
}

/** True quando `agora` está dentro da jornada (fora de sábado e domingo). */
export function dentroDoExpediente(expediente, agora = new Date()) {
  if (!expediente) return false;
  const dow = agora.getDay();
  if (dow === 0 || dow === 6) return false;
  const min = agora.getHours() * 60 + agora.getMinutes();
  return min >= expediente.ini && min <= expediente.fim;
}
